import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Upload, FileAudio, X, AlertCircle, ExternalLink, ListMusic, Search, Music2 } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config';
import PlaylistImporter from './PlaylistImporter';

const formatDuration = (sec) => {
  if (!sec || isNaN(sec)) return '';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

export const AudioUploader = ({ playlistId, onUploadSuccess, onClose }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [nota, setNota] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadMethod, setUploadMethod] = useState('file'); // 'file' | 'youtube' | 'search' | 'playlist'
  const [youtubeUrl, setYoutubeUrl] = useState('');

  // Estado para modal de YouTube bloqueado
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [blockedVideoInfo, setBlockedVideoInfo] = useState(null);

  // Estado para búsqueda YouTube (elegir entre 3 candidatos)
  const [searchTitle, setSearchTitle] = useState('');
  const [searchArtist, setSearchArtist] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [searching, setSearching] = useState(false);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar formato MP3 o M4A
    const isMP3 = file.type.includes('audio/mpeg') || file.name.toLowerCase().endsWith('.mp3');
    const isM4A = file.type.includes('audio/mp4') || file.type.includes('audio/x-m4a') || file.type.includes('audio/m4a') || file.type.includes('audio/aac') || file.name.toLowerCase().endsWith('.m4a');
    if (!isMP3 && !isM4A) {
      toast.error('Solo se permiten archivos MP3 o M4A');
      return;
    }

    // Validar tamaño (50MB)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`Archivo muy grande. Máximo 50MB (tamaño: ${(file.size / 1024 / 1024).toFixed(1)}MB)`);
      return;
    }

    setSelectedFile(file);
  };

  const handleUploadYoutube = async (overrideUrl) => {
    // Feb-2026 BUGFIX: si nos llaman como onClick directo (sin arrow function),
    // el primer arg es un SyntheticEvent, no una string. Solo aceptamos string.
    const overrideStr = typeof overrideUrl === 'string' ? overrideUrl : '';
    const urlToUse = overrideStr || youtubeUrl;
    if (!urlToUse) {
      toast.error('Introduce una URL de YouTube');
      return;
    }
    if (!urlToUse.includes('youtube.com') && !urlToUse.includes('youtu.be')) {
      toast.error('URL no válida. Debe ser un enlace de YouTube');
      return;
    }
    if (!playlistId) {
      toast.error('Error: No se encontró el ID del evento');
      console.error('❌ playlistId is missing:', playlistId);
      return;
    }

    setUploading(true);
    
    // Mostrar toast de progreso persistente
    const toastId = toast.loading('⏬ Conectando con YouTube...', {
      duration: Infinity // No auto-cerrar
    });

    try {
      const token = localStorage.getItem('token');
      
      console.log('📤 Añadiendo link de YouTube:', {
        url: `${BACKEND_URL}/api/peticiones/youtube-link/start`,
        youtube_url: urlToUse,
        playlist_id: playlistId,
        nota: nota
      });
      
      // Actualizar mensaje
      toast.loading('🔍 Descargando audio de YouTube...', { id: toastId });
      
      // Timeout total de la operación (start + poll) — el edge Cloudflare
      // sólo se ve afectado en el /start (que devuelve en <1s) — el resto es
      // polling ligero.
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 240000);
      
      // 1) Start async job (devuelve job_id en <200ms)
      const startResp = await axios.post(
        `${BACKEND_URL}/api/peticiones/youtube-link/start`,
        null,
        {
          params: {
            youtube_url: urlToUse,
            playlist_id: playlistId,
            nota: nota
          },
          headers: {
            Authorization: `Bearer ${token}`
          },
          signal: controller.signal,
          timeout: 20000
        }
      );
      const jobId = startResp.data?.job_id;
      if (!jobId) throw new Error('No se recibió job_id');
      
      // 2) Poll cada 3s hasta 4 min
      const maxAttempts = 80;
      let finalResult = null;
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise(r => setTimeout(r, 3000));
        try {
          const st = await axios.get(
            `${BACKEND_URL}/api/peticiones/youtube-link/status/${jobId}`,
            { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal, timeout: 20000 }
          );
          const s = st.data?.status;
          if (s === 'done') { finalResult = st.data.result; break; }
          if (s === 'failed') {
            const err = new Error(st.data.error || 'La operación falló');
            err.jobFailed = true;
            throw err;
          }
        } catch (pollErr) {
          if (pollErr.jobFailed) throw pollErr;
          if (pollErr.response?.status === 404) throw pollErr;
          // Errores de red transitorios: seguimos polling
          if (!pollErr.response) continue;
          throw pollErr;
        }
      }
      if (!finalResult) throw new Error('Tiempo de espera agotado');
      
      clearTimeout(timeoutId);
      
      // Compatibilidad total con el shape antiguo → reutiliza la lógica sin cambios
      const response = { data: finalResult };
      
      const audioDownloaded = response.data.audio_downloaded;
      const youtubeBlocked = response.data.youtube_blocked;
      const youtubeUnavailable = response.data.youtube_unavailable;
      const youtubeUserMessage = response.data.youtube_user_message;
      
      if (audioDownloaded) {
        toast.success('✅ Audio de YouTube descargado correctamente', { id: toastId });
      } else if (youtubeUnavailable) {
        // Vídeo no descargable desde el servidor (geo-bloqueo / restricción).
        // No es fallo técnico: el usuario debe bajarlo manualmente desde su navegador en España.
        toast.dismiss(toastId);
        setBlockedVideoInfo({
          url: youtubeUrl,
          manualUrl: response.data.manual_download_url,
          title: response.data.peticion?.titulo || 'Video de YouTube',
          reason: 'unavailable',
          userMessage: youtubeUserMessage || 'Este vídeo está restringido geográficamente. Descárgalo desde tu navegador en España y súbelo manualmente.',
        });
        setShowBlockedModal(true);
      } else if (youtubeBlocked) {
        // Mostrar modal de ayuda para descarga manual
        toast.dismiss(toastId);
        setBlockedVideoInfo({
          url: youtubeUrl,
          manualUrl: response.data.manual_download_url,
          title: response.data.peticion?.titulo || 'Video de YouTube',
          reason: 'blocked',
          userMessage: youtubeUserMessage || 'YouTube ha bloqueado la descarga. Actualiza tus cookies en Ajustes si hace más de 30 días que las subiste.',
        });
        setShowBlockedModal(true);
      } else {
        // BUGFIX 2026-10-21: antes, cualquier fallo que NO fuera exactamente
        // "blocked" o "unavailable" (ej. proxy caído, cookies inválidas,
        // "no_format") mostraba un ✅ falso positivo ("Link añadido, sin
        // audio descargado") sin indicar que la descarga realmente falló ni
        // el motivo. Ahora cualquier fallo de descarga muestra el modal con
        // el mensaje real calculado por el backend.
        toast.dismiss(toastId);
        setBlockedVideoInfo({
          url: youtubeUrl,
          manualUrl: response.data.manual_download_url,
          title: response.data.peticion?.titulo || 'Video de YouTube',
          reason: 'error',
          userMessage: youtubeUserMessage || 'No se pudo descargar el audio automáticamente. Sube el MP3 manualmente.',
        });
        setShowBlockedModal(true);
      }
      console.log('✅ YouTube peticion added:', response.data);
      
      // Resetear formulario
      setYoutubeUrl('');
      setNota('');
      
      // Notificar éxito
      if (onUploadSuccess) {
        onUploadSuccess(response.data);
      }
      
    } catch (error) {
      console.error('❌ Error downloading YouTube:', error);
      console.error('Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      // Mostrar mensaje de error más descriptivo
      let errorMessage = 'Error al procesar link de YouTube';
      
      if (error.code === 'ECONNABORTED' || error.code === 'ERR_CANCELED' || error.message.includes('abort') || error.message.includes('canceled')) {
        errorMessage = '⏱️ Timeout: No se pudo obtener información del video. Verifica el enlace.';
      } else if (error.response?.status === 400 && error.response?.data?.detail?.includes('URL de YouTube inválida')) {
        errorMessage = '🚫 URL de YouTube inválida. Verifica el enlace.';
      } else if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // Error de validación de FastAPI
          const fields = error.response.data.detail.map(e => e.loc.join('.')).join(', ');
          errorMessage = `Campos requeridos: ${fields}`;
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage, { id: toastId });
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Selecciona un archivo primero');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('playlist_id', playlistId);
    formData.append('nota', nota);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/audio/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        }
      );

      toast.success('✅ Archivo subido correctamente');
      console.log('✅ Audio uploaded:', response.data);
      
      // Resetear formulario
      setSelectedFile(null);
      setNota('');
      setUploadProgress(0);
      
      // Notificar éxito
      if (onUploadSuccess) {
        onUploadSuccess(response.data);
      }
      
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('❌ Error uploading audio:', error);
      const errorMsg = error.response?.data?.detail || 'Error al subir el archivo';
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setUploadProgress(0);
  };

  return (
    <div className="space-y-4">
      {/* Selector de método */}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          onClick={() => setUploadMethod('file')}
          className={`flex-1 min-w-[120px] ${uploadMethod === 'file' ? 'bg-pink-600' : 'bg-slate-700'}`}
        >
          <Upload className="w-4 h-4 mr-2" />
          Subir MP3/M4A
        </Button>
        <Button
          type="button"
          onClick={() => setUploadMethod('youtube')}
          className={`flex-1 min-w-[120px] ${uploadMethod === 'youtube' ? 'bg-red-600' : 'bg-slate-700'}`}
        >
          <FileAudio className="w-4 h-4 mr-2" />
          Link YouTube
        </Button>
        <Button
          type="button"
          onClick={() => setUploadMethod('search')}
          className={`flex-1 min-w-[120px] ${uploadMethod === 'search' ? 'bg-cyan-600' : 'bg-slate-700'}`}
          data-testid="upload-method-search-btn"
        >
          <Search className="w-4 h-4 mr-2" />
          Buscar YouTube
        </Button>
        <Button
          type="button"
          onClick={() => setUploadMethod('playlist')}
          className={`flex-1 min-w-[120px] ${uploadMethod === 'playlist' ? 'bg-indigo-600' : 'bg-slate-700'}`}
          data-testid="upload-method-playlist-btn"
        >
          <ListMusic className="w-4 h-4 mr-2" />
          Importar Playlist
        </Button>
      </div>

      {/* Información */}
      {uploadMethod === 'file' ? (
        <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-blue-300">
              <p className="font-semibold mb-1">Subir archivo de audio</p>
              <ul className="list-disc list-inside space-y-1 text-blue-400">
                <li>Archivos MP3 o M4A (audio iPhone)</li>
                <li>Tamaño máximo: 50MB</li>
                <li>Se eliminará 7 días después del evento</li>
              </ul>
            </div>
          </div>
        </div>
      ) : uploadMethod === 'youtube' ? (
        <div className="bg-red-900/20 border border-red-700/30 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-red-300">
              <p className="font-semibold mb-1">Descargar de YouTube</p>
              <ul className="list-disc list-inside space-y-1 text-red-400">
                <li>Pega el enlace del video de YouTube</li>
                <li>Se descargará el audio como MP3</li>
                <li>Podrás reproducirlo directamente en la app</li>
                <li>La descarga puede tardar 1-2 minutos</li>
              </ul>
            </div>
          </div>
        </div>
      ) : null}

      {/* Formulario según método */}
      {uploadMethod === 'playlist' ? (
        <PlaylistImporter
          playlistId={playlistId}
          onImportSuccess={(data) => {
            if (typeof onUploadSuccess === 'function') onUploadSuccess(data);
            if (typeof onClose === 'function') onClose();
          }}
          onClose={onClose}
        />
      ) : uploadMethod === 'search' ? (
        <div className="space-y-3" data-testid="search-youtube-panel">
          <div className="bg-cyan-900/20 border border-cyan-700/30 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <Search className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-cyan-300">
                <p className="font-semibold mb-1">Buscar en YouTube</p>
                <ul className="list-disc list-inside space-y-1 text-cyan-400">
                  <li>Introduce título (y artista opcional)</li>
                  <li>Elige entre las 3 mejores coincidencias</li>
                  <li>Se descarga el MP3 automáticamente</li>
                </ul>
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="yt-search-title" className="text-slate-200">
              Título de la canción
            </Label>
            <Input
              id="yt-search-title"
              value={searchTitle}
              onChange={(e) => setSearchTitle(e.target.value)}
              placeholder="Ej: Mi Corazón Encantado"
              className="bg-slate-700 border-slate-600 text-white"
              disabled={searching || uploading}
              data-testid="yt-search-title-input"
            />
          </div>
          <div>
            <Label htmlFor="yt-search-artist" className="text-slate-200">
              Artista (opcional)
            </Label>
            <Input
              id="yt-search-artist"
              value={searchArtist}
              onChange={(e) => setSearchArtist(e.target.value)}
              placeholder="Ej: Dragon Ball GT"
              className="bg-slate-700 border-slate-600 text-white"
              disabled={searching || uploading}
              data-testid="yt-search-artist-input"
            />
          </div>

          <Button
            onClick={async () => {
              const title = (searchTitle || '').trim();
              if (!title) { toast.error('Introduce al menos el título'); return; }
              setSearching(true);
              setSelectedCandidate(null);
              setSearchResults([]);
              try {
                const token = localStorage.getItem('token');
                const res = await axios.post(
                  `${BACKEND_URL}/api/peticiones/youtube-search`,
                  { title, artist: (searchArtist || '').trim(), limit: 3 },
                  { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 }
                );
                const list = res.data?.candidates || [];
                if (list.length === 0) {
                  toast.warning('YouTube no devolvió candidatos. Prueba con otro título.');
                } else {
                  setSearchResults(list);
                  setSelectedCandidate(list[0].youtube_url);
                }
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Error buscando en YouTube');
              } finally {
                setSearching(false);
              }
            }}
            disabled={searching || uploading || !searchTitle.trim()}
            className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
            data-testid="yt-search-run-btn"
          >
            {searching ? (
              <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>Buscando…</>
            ) : (
              <><Search className="w-4 h-4 mr-2" />Buscar</>
            )}
          </Button>

          {searchResults.length > 0 && (
            <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1" data-testid="yt-search-results">
              {searchResults.map((c) => {
                const isSel = selectedCandidate === c.youtube_url;
                return (
                  <button
                    key={c.youtube_url}
                    type="button"
                    onClick={() => setSelectedCandidate(c.youtube_url)}
                    disabled={uploading}
                    className={`w-full text-left rounded-md border p-2 flex items-start gap-2 transition-colors ${
                      isSel ? 'border-cyan-500 bg-cyan-950/40' : 'border-slate-700 bg-slate-800/60 hover:bg-slate-800'
                    }`}
                    data-testid={`yt-candidate-${c.youtube_url}`}
                  >
                    {c.thumbnail ? (
                      <img src={c.thumbnail} alt="" className="w-20 h-14 object-cover rounded flex-shrink-0" />
                    ) : (
                      <div className="w-20 h-14 rounded bg-slate-700 flex items-center justify-center flex-shrink-0">
                        <Music2 className="w-5 h-5 text-slate-400" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-white text-sm font-medium truncate">{c.title}</div>
                      <div className="text-slate-400 text-xs truncate">
                        {c.uploader}{c.duration ? ` · ${formatDuration(c.duration)}` : ''}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {uploading && (
            <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cyan-500"></div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">Descargando audio…</p>
                  <p className="text-xs text-slate-400">Puede tardar hasta 3 minutos según el vídeo</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <Button onClick={onClose} variant="outline" disabled={uploading} className="border-slate-600 text-slate-300 hover:bg-slate-700">
              Cancelar
            </Button>
            <Button
              onClick={() => handleUploadYoutube(selectedCandidate)}
              disabled={!selectedCandidate || uploading}
              className="bg-cyan-600 hover:bg-cyan-700 text-white disabled:opacity-50"
              data-testid="yt-search-download-btn"
            >
              {uploading ? 'Descargando…' : 'Descargar seleccionada'}
            </Button>
          </div>
        </div>
      ) : uploadMethod === 'file' ? (
        <>
          {/* Selector de archivo */}
      {!selectedFile ? (
        <div className="border-2 border-dashed border-slate-600 rounded-lg p-8 hover:border-pink-500 transition-colors">
          <input
            type="file"
            accept="audio/mpeg,audio/mp3,audio/mp4,audio/x-m4a,audio/aac,.m4a,.mp3"
            onChange={handleFileSelect}
            disabled={uploading}
            className="hidden"
            id="audio-upload-input"
          />
          <label
            htmlFor="audio-upload-input"
            className="cursor-pointer flex flex-col items-center"
          >
            <Upload className="w-12 h-12 text-slate-400 mb-3" />
            <span className="text-slate-300 font-medium mb-1">
              Click para seleccionar archivo
            </span>
            <span className="text-xs text-slate-500">
              MP3 o M4A (máx. 50MB)
            </span>
          </label>
        </div>
      ) : (
        <Card className="bg-slate-700/50 border-slate-600 p-4">
          <div className="flex items-start gap-3">
            {selectedFile?.name?.toLowerCase().endsWith('.m4a') ? (
              <FileAudio className="w-8 h-8 text-blue-400 flex-shrink-0" />
            ) : (
              <FileAudio className="w-8 h-8 text-pink-400 flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0 overflow-hidden">
              <p className="text-white font-medium truncate" title={selectedFile.name}>
                {selectedFile.name}
              </p>
              <p className="text-slate-400 text-sm">
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </p>
              
              {uploading && (
                <div className="mt-2">
                  <div className="w-full bg-slate-600 rounded-full h-2">
                    <div
                      className="bg-pink-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">{uploadProgress}% subiendo...</p>
                </div>
              )}
            </div>
            {!uploading && (
              <Button
                size="sm"
                variant="ghost"
                onClick={handleRemoveFile}
                className="text-slate-400 hover:text-white flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Campo de nota */}
      {selectedFile && !uploading && (
        <div className="space-y-2">
          <Label htmlFor="nota" className="text-slate-200">
            Nota (opcional)
          </Label>
          <Input
            id="nota"
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            placeholder="Ej: Regalo madres, Primera baile, etc."
            className="bg-slate-700 border-slate-600 text-white"
            maxLength={100}
            data-testid="mp3-nota-input"
          />
          <p className="text-xs text-slate-500">
            Esta nota aparecerá junto al archivo en la lista de peticiones
          </p>
        </div>
      )}

      {/* Botones para archivo */}
      {uploadMethod === 'file' && (
        <div className="flex gap-2 justify-end">
          <Button
            onClick={onClose}
            variant="outline"
            disabled={uploading}
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            className="bg-pink-600 hover:bg-pink-700 text-white"
          >
            {uploading ? `Subiendo... ${uploadProgress}%` : 'Subir Archivo'}
          </Button>
        </div>
      )}
      </>
      ) : (
        <>
          {/* Formulario YouTube */}
          <div className="space-y-3">
            <div>
              <Label htmlFor="youtube-url" className="text-slate-200">
                URL de YouTube
              </Label>
              <Input
                id="youtube-url"
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
                className="bg-slate-700 border-slate-600 text-white"
                disabled={uploading}
              />
              <p className="text-xs text-slate-500 mt-1">
                Pega el enlace completo del video de YouTube
              </p>
            </div>

            <div>
              <Label htmlFor="nota-youtube" className="text-slate-200">
                Nota (opcional)
              </Label>
              <Input
                id="nota-youtube"
                value={nota}
                onChange={(e) => setNota(e.target.value)}
                placeholder="Ej: Primer baile, Entrada novios, etc."
                className="bg-slate-700 border-slate-600 text-white"
                disabled={uploading}
                maxLength={100}
                data-testid="youtube-nota-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Esta nota aparecerá junto a la canción en la lista de peticiones
              </p>
            </div>
          </div>

          {/* Indicador de progreso para YouTube */}
          {uploading && (
            <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-500"></div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">Descargando audio de YouTube...</p>
                  <p className="text-xs text-slate-400">Esto puede tardar hasta 2 minutos según la duración del video</p>
                </div>
              </div>
              <div className="mt-3 w-full bg-slate-600 rounded-full h-2 overflow-hidden">
                <div className="bg-red-500 h-2 rounded-full animate-pulse" style={{width: '100%'}}></div>
              </div>
            </div>
          )}

          {/* Botones para YouTube */}
          <div className="flex gap-2 justify-end">
            <Button
              onClick={onClose}
              variant="outline"
              disabled={uploading}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              Cancelar
            </Button>
            <Button
              onClick={() => handleUploadYoutube()}
              disabled={!youtubeUrl || uploading}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Descargando...
                </>
              ) : (
                'Descargar de YouTube'
              )}
            </Button>
          </div>
        </>
      )}

      {/* Disclaimer legal */}
      <div className="bg-yellow-900/20 border border-yellow-700/30 rounded-lg p-3">
        <p className="text-xs text-yellow-300">
          ⚠️ Al añadir este audio, confirmas que tienes los derechos necesarios para su uso en eventos privados.
        </p>
      </div>

      {/* Modal de YouTube Bloqueado */}
      <Dialog open={showBlockedModal} onOpenChange={setShowBlockedModal}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-400" />
              {blockedVideoInfo?.reason === 'unavailable'
                ? 'Vídeo no disponible desde el servidor'
                : blockedVideoInfo?.reason === 'blocked'
                  ? 'YouTube bloqueó la descarga'
                  : 'No se pudo descargar el audio'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {blockedVideoInfo?.userMessage ||
                'El servidor no puede descargar este video directamente. Pero puedes hacerlo manualmente en 3 pasos:'}
            </DialogDescription>
          </DialogHeader>
          
          {blockedVideoInfo?.reason === 'unavailable' && (
            <div className="mb-2 p-3 bg-amber-900/30 border border-amber-700/50 rounded text-xs text-amber-300">
              <strong>Nota:</strong> Las cookies de YouTube NO resuelven este caso — es un
              bloqueo por contenido/región, no por identidad. La única alternativa automática
              sería un proxy residencial español.
            </div>
          )}
          
          <div className="space-y-4 py-4">
            {/* Paso 1 */}
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-pink-600 text-white flex items-center justify-center text-sm font-bold">1</div>
              <div>
                <p className="text-white font-medium">Descarga el MP3</p>
                <p className="text-slate-400 text-sm mb-2">Haz clic en el botón para abrir el descargador externo:</p>
                <Button
                  onClick={() => {
                    if (blockedVideoInfo?.manualUrl) {
                      window.open(blockedVideoInfo.manualUrl, '_blank');
                    }
                  }}
                  className="bg-red-600 hover:bg-red-700 text-white text-sm"
                  size="sm"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Abrir descargador
                </Button>
              </div>
            </div>

            {/* Paso 2 */}
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-pink-600 text-white flex items-center justify-center text-sm font-bold">2</div>
              <div>
                <p className="text-white font-medium">Espera la conversión</p>
                <p className="text-slate-400 text-sm">Descarga el archivo MP3 a tu dispositivo</p>
              </div>
            </div>

            {/* Paso 3 */}
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-pink-600 text-white flex items-center justify-center text-sm font-bold">3</div>
              <div>
                <p className="text-white font-medium">Sube el MP3 aquí</p>
                <p className="text-slate-400 text-sm">Usa el método "Subir MP3" para añadirlo al evento</p>
              </div>
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowBlockedModal(false)}
              className="border-slate-600 text-slate-300"
            >
              Cerrar
            </Button>
            <Button
              onClick={() => {
                setShowBlockedModal(false);
                setUploadMethod('file');
                setYoutubeUrl('');
              }}
              className="bg-pink-600 hover:bg-pink-700 text-white"
            >
              <Upload className="w-4 h-4 mr-2" />
              Ir a subir MP3
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
